import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useIntl } from "react-intl";
import { getBlob, uploadFieldFile, getFile } from "../../../services/restAPI/RestApi";
import { Button } from "../Button/Button";
import { Message } from "../Messages/Messages";
import Spin from "../Spinner/Spinner";
import "./Upload.less";
import { Icon_Download } from "../Icons/icons";
import HLSPlayer from "../../Video/HLSPlayer";
import clsx from "clsx";
import Modal from "../Modal";

interface UploadProps {
   className?: string;
   style?: React.CSSProperties;
   disabled?: boolean;
   onUpload?: (data: any) => void;
   onLoad?: (data: any) => void;
   onRemove?: (file) => void;
   updateFieldValue?: (value: string | undefined) => void;
   uploadUrl?: string;
   children: React.ReactNode;
   defaultFileId?: string;
   cloudServer?: boolean;
   fileType?: string;
   defaultFileUrl?: string;
   defaultFile?: { url: string; previewUrl: string; name: string; id: number | string };
   addBarcode?: boolean;
}

interface MovieFile {
   id: string;
   name: string;
   extension: string;
   mimeType: string;
   size: number;
   ready: boolean;
   streamable: boolean;
   streamUrl: string;
   createdAt: Date;
   updatedAt: Date;
}

export const Upload: React.FC<UploadProps> = ({
   onUpload,
   onRemove,
   style,
   className,
   disabled,
   updateFieldValue,
   uploadUrl,
   children,
   defaultFileId,
   defaultFileUrl,
   defaultFile,
   addBarcode,
   onLoad,
   cloudServer = false,
   fileType,
}) => {
   const intl = useIntl();
   const inputRef = useRef<HTMLInputElement>(null);
   const [loading, setLoading] = useState(false);
   const [file, setFile] = useState<{
      url: string;
      previewUrl: string;
      name: string;
      id: number | string;
   } | null>(defaultFile ?? null);

   const [movieFile, setMovieFile] = useState<MovieFile | null>(null);

   const [selectedImageOrPdf, setSelectedImageOrPdf] = useState<string | null>(null);

   const modalRoot = useRef(document.getElementById("modal-root") || document.body).current;

   const checkFileType = (file: string): string => {
      const extension = file.split(".").pop()?.toLowerCase();

      if (fileType) {
         return fileType;
      } else if (extension) {
         if (["jpg", "jpeg", "png", "webp", "gif"].includes(extension)) {
            return "image";
         } else if (extension === "pdf") {
            return "pdf";
         }
      }
      return "other";
   };

   const openFile = (file) => {
      const fileType = checkFileType(file.name);

      if (fileType === "image" || fileType === "pdf") {
         setSelectedImageOrPdf(file.previewUrl);
      } else {
         const link = document.createElement("a");
         link.href = file.url;
         link.target = "_blank";
         link.click();
      }
   };

   const closeOverlay = () => {
      setSelectedImageOrPdf(null);
   };

   const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
         // We have to modify type if it's svg, because it's not supported by backend
         const modifiedFile = new File([selectedFile], selectedFile.name, {
            type: selectedFile.type === "image/svg+xml" ? "image/svg" : selectedFile.type,
            lastModified: selectedFile.lastModified,
         });

         if (typeof onUpload === "function") onUpload(modifiedFile);
         handleSubmit(modifiedFile);
      }
   };

   const handleSubmit = async (fileToUpload: File) => {
      if (!uploadUrl) return;
      try {
         Message.loading(intl.formatMessage({ id: "global.loading" }));
         const formData = new FormData();
         formData.append("multipartFile", fileToUpload);
         if (addBarcode) {
            formData.append("addBarcode", true.toString());
         }

         const res = await uploadFieldFile(formData, uploadUrl);
         if (typeof onUpload === "function") onUpload(res.data);
         setFile({
            url: URL.createObjectURL(fileToUpload),
            previewUrl: URL.createObjectURL(fileToUpload),
            name: fileToUpload.name,
            id: res.data.uploadFile?.dtbId || res.data.addFiles?.[0]?.dtbId || fileToUpload.name,
         });
         if (typeof updateFieldValue === "function") updateFieldValue(res.data.uploadFile.dtbId);

         Message.destroy();
         Message.success(intl.formatMessage({ id: "global.done" }));
      } catch (error) {
         Message.error(intl.formatMessage({ id: "global.error" }));
      } finally {
         setTimeout(() => {
            Message.destroy();
         }, 1500);
      }
   };

   const handleDelete = () => {
      onRemove && onRemove(file);
      setFile(null);
      setMovieFile(null);
      if (typeof updateFieldValue === "function") updateFieldValue(undefined);
   };

   useEffect(() => {
      const getDefaultFile = async () => {
         try {
            setLoading(true);
            let file;
            let finalFileName;

            if (cloudServer) {
               file = (await getFile(defaultFileId))?.data;
               // remove when back php fixes file links here and in FileField.jsx
               if (file && typeof file === "string" && file.includes("http:")) {
                  file = file.replaceAll("http:", "https:");
               }
               const urlSplit = file.split("/");
               finalFileName = urlSplit[urlSplit.length - 1];
            } else {
               if (defaultFileId && +defaultFileId !== 0) {
                  file = (await getBlob(defaultFileId)).data.default;
               }
               if (
                  (file?.hasOwnProperty("extension") && file?.extension === "mp4") ||
                  (file?.hasOwnProperty("streamUrl") && file?.streamUrl?.includes(".m3u8"))
               ) {
                  setMovieFile(file);
               }

               if (file && typeof file === "string" && file.includes("http:")) {
                  file = file.replaceAll("http:", "https:");
               }

               const fileNameRegEx: RegExpMatchArray = file.match(/\/([^\/.]+(\.\w+))$/);

               finalFileName = fileNameRegEx[1] ? fileNameRegEx[1] : "File";
            }

            if (typeof onLoad === "function") onLoad(finalFileName);
            setFile({
               url: defaultFileUrl || file,
               previewUrl: file,
               name: finalFileName,
               id: defaultFileId ?? "",
            });
         } catch {
            setFile(null);
         } finally {
            setLoading(false);
         }
      };

      if (!file && defaultFileId) {
         getDefaultFile();
      }
   }, [defaultFileId]);

   const handleFileDownload = () => {
      if (file) {
         const link = document.createElement("a");
         link.href = file.url ? file.url : "";
         link.setAttribute("download", file?.name ? file.name : "");
         link.setAttribute("target", "_blank");
         document.body.appendChild(link);
         link.click();
         link.parentNode?.removeChild(link);
      } else if (movieFile) {
         try {
            const link = document.createElement("a");
            link.href = movieFile.streamUrl;
            link.setAttribute("download", movieFile.name);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
         } catch (error) {
            console.error("Błąd podczas pobierania filmu:", error);
         }
      }
   };

   return (
      <div className={`bp2upload ${className ? className : ""}`} style={style}>
         <div className="bp2upload__container">
            {file && (
               <div className="bp2upload__preview__icon">
                  {file.url && checkFileType(file.name) === "image" ? (
                     <img
                        className="bp2upload__container__image"
                        src={file?.previewUrl || defaultFileUrl}
                        alt="Preview"
                        onClick={() => openFile(file)}
                     />
                  ) : file.url && checkFileType(file.name) === "pdf" ? (
                     <span className="icon icon-eye" onClick={() => openFile(file)}></span>
                  ) : file.url ? (
                     <span
                        className="icon icon-long-arrow-down"
                        onClick={() => openFile(file)}
                     ></span>
                  ) : (
                     <span className="icon icon-files"></span>
                  )}
               </div>
            )}

            {loading && (
               <div className="spin-middle">
                  <Spin />
               </div>
            )}
         </div>
         <div className={clsx("bp2upload__preview", !file ? "bp2upload__preview--no-border" : "")}>
            <div className="bp2upload__preview__actions">
               <div style={{ lineHeight: 0 }} onClick={() => inputRef?.current?.click()}>
                  {children}
               </div>
               <input
                  data-testid="upload"
                  ref={inputRef}
                  type="file"
                  onChange={handleFileChange}
                  disabled={disabled}
               />
               {file && (
                  <>
                     <Button disabled={disabled} onClick={handleFileDownload}>
                        <Icon_Download fill="none" stroke="black" />
                     </Button>
                     <Button
                        disabled={disabled}
                        data-testid="upload-delete"
                        buttonType="danger"
                        onClick={handleDelete}
                     >
                        <span className="icon icon-trash" />
                     </Button>
                  </>
               )}
            </div>
         </div>

         {/*  1. If the file is a movie, we display a thumbnail (first frame of the video).
              2. On thumbnail click, we redirect the user.*/}
         {movieFile && (
            <div className="bp2upload__preview">
               {/* 2. here: */}
               <a
                  href={`https://player.carlsbergpolska-biznesportal.pl/?${new URLSearchParams({
                     videoUrl: movieFile?.streamUrl,
                  }).toString()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ width: "40px", height: "40px", cursor: "pointer" }}
               >
                  {/* 1. The thumbnail is generated by rendering the video element with controls disabled, preventing user interaction. */}
                  <HLSPlayer src={movieFile?.streamUrl} controls={false} />
               </a>
               {movieFile?.name}{" "}
               <Button disabled={disabled} onClick={handleFileDownload}>
                  <Icon_Download fill="none" stroke="black" />
               </Button>
               <Button
                  disabled={disabled}
                  data-testid="upload-delete"
                  buttonType="danger"
                  onClick={handleDelete}
               >
                  <span className="icon icon-trash" />
               </Button>
            </div>
         )}

         {selectedImageOrPdf &&
            ReactDOM.createPortal(
               <div
                  className="upload-preview-overlay"
                  onClick={closeOverlay}
                  role="presentation"
                  aria-hidden="true"
               />,
               modalRoot,
            )}

         {selectedImageOrPdf && (
            <Modal
               isOpen={true}
               onCancel={() => closeOverlay()}
               backgroundShadow={false}
               calculatePosition
               wrapClassName="no-resize upload-preview-modal"
               topBarContent={intl.formatMessage({ id: "global.filePreview" })}
            >
               {file && checkFileType(file.name) === "image" ? (
                  <img
                     src={selectedImageOrPdf}
                     alt={file?.name || "Preview"}
                     className="upload-preview-image"
                  />
               ) : (
                  <iframe
                     src={selectedImageOrPdf}
                     title={file?.name || "Preview"}
                     className="upload-preview-iframe"
                  />
               )}
            </Modal>
         )}
      </div>
   );
};
